import { useEffect } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  TextField,
  FormLabel,
  FormControlLabel,
  FormControl,
  FormGroup,
  Checkbox,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { SignupUserDto } from 'types';
import { UserForm } from './types';
import { getPasswordStrength } from '../auth/utils/passwordUtils';
import { Controller, useForm } from 'react-hook-form';

import { useUser } from './api/getUser';
import { useCreateUser } from './api/createUser';
import { useUpdateUser } from './api/updateUser';

interface Props {
  open: boolean;
  handleClose: () => void;
  selectedUserId: number | null;
}

const defaultValues: UserForm = {
  id: '',
  name: '',
  email: '',
  password: '',
  role: '0',
  frozen: false,
};

const UserDialog = ({ open, handleClose, selectedUserId }: Props) => {
  const {
    control,
    handleSubmit,
    getValues,
    setError,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm<UserForm>({ defaultValues });
  const { data: initialUser } = useUser({ userId: selectedUserId || -1 });
  const createUserMutation = useCreateUser({});
  const updateUserMutation = useUpdateUser({});

  const onSubmit = async () => {
    const formState = getValues();

    if (selectedUserId === null || selectedUserId === undefined) {
      if (formState.name && formState.email && formState.password) {
        const userToCreate: SignupUserDto = {
          name: formState.name,
          email: formState.email,
          password: formState.password,
          role: Number(formState.role),
        };
        try {
          await createUserMutation.mutateAsync({
            data: userToCreate,
          });
        } catch (e: any) {
          if (e.response.data.error.includes('Email')) {
            setError('email', {
              type: 'submission',
              message: 'Email address already in use.',
            });
          } else {
            setError('name', {
              type: 'submission',
              message: 'User name already in use.',
            });
          }
          return;
        }
      }
      reset(defaultValues);
    } else {
      if (!formState?.id) return;
      await updateUserMutation.mutateAsync({
        data: {
          ...formState,
          role: formState.role ? Number(formState.role) : undefined,
        },
        userId: Number(formState.id),
      });
    }
    handleClose();
  };

  const handleCancel = () => {
    handleClose();
  };

  useEffect(() => {
    if (!initialUser) reset(defaultValues);
    else reset(initialUser);
  }, [reset, initialUser, open]);

  return (
    <Dialog open={open} onClose={handleClose}>
      {selectedUserId === null && <DialogTitle>New User</DialogTitle>}
      {selectedUserId !== null && <DialogTitle>Update User</DialogTitle>}
      <DialogContent>
        <form>
          <Controller
            name='name'
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <TextField
                autoFocus
                margin='dense'
                id='name'
                name='name'
                label='Name'
                fullWidth
                required
                value={value}
                onChange={onChange}
                autoComplete='off'
                error={!!errors.name}
                helperText={errors?.name?.message}
              />
            )}
          />
          <Controller
            name='email'
            control={control}
            rules={{
              required: true,
              validate: (value) =>
                value
                  ? /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
                      value
                    )
                  : false,
            }}
            render={({ field: { onChange, value } }) => (
              <TextField
                margin='dense'
                id='email'
                name='email'
                label='Email Address'
                fullWidth
                required
                value={value}
                onChange={onChange}
                autoComplete='off'
                helperText={errors?.email?.message}
                error={!!errors.email}
              />
            )}
          />
          <Controller
            name='password'
            control={control}
            rules={{
              required: false,
              validate: (value) => (selectedUserId === null ? !!value : true),
            }}
            render={({ field: { onChange, value } }) => (
              <TextField
                type='password'
                margin='dense'
                id='password'
                name='password'
                label='Password'
                fullWidth
                required={!watch('id')}
                value={value}
                onChange={onChange}
                autoComplete='off'
                error={!!errors.password}
              />
            )}
          />
          {!!watch('id') && (
            <Typography align='left' style={{ marginLeft: '1rem' }}>
              If this password field is left blank, the user's password will
              stay the same.
            </Typography>
          )}
          {!!watch('password') && (
            <Typography align='left' style={{ marginLeft: '1rem' }}>
              {`Password Strength: ${getPasswordStrength(watch('password'))}`}
            </Typography>
          )}
          <Controller
            name='role'
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <FormControl margin='dense' fullWidth component='fieldset'>
                <FormLabel component='legend'>Role</FormLabel>
                <RadioGroup row name='role' value={value} onChange={onChange}>
                  <FormControlLabel
                    value='0'
                    control={<Radio />}
                    label='Viewer'
                  />
                  <FormControlLabel
                    value='1'
                    control={<Radio />}
                    label='Operator'
                  />
                  <FormControlLabel
                    value='2'
                    control={<Radio />}
                    label='Admin'
                  />
                </RadioGroup>
              </FormControl>
            )}
          />
          <Controller
            name='frozen'
            control={control}
            render={({ field: { onChange, value } }) => (
              <FormGroup>
                <FormControlLabel
                  label='Freeze Account'
                  control={<Checkbox checked={value} onChange={onChange} />}
                />
              </FormGroup>
            )}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} variant='contained' color='secondary'>
          Cancel
        </Button>
        <Button
          onClick={() => {
            handleSubmit(onSubmit)();
          }}
          disabled={!isDirty}
          variant='contained'
          color='primary'
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDialog;
